import PropTypes from "prop-types";
import CustomImage from "../../../atoms/customImage";
import Typography from "../../../atoms/typography";
import RadioItemList from "../../radioItemList";
import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  setIsSavedAddressUserName,
  setIsShowDesktopLocationLockAction,
  setIsShowMobileLocationLockAction,
  setSelectedDomesticLocation,
} from "../../../../store/slices/locationLock";
import CookieUtil from "../../../../utils/storage/cookieUtil";
import {
  LAST_VISITED_ADDRESS,
  LAST_VISITED_INTERNATIONAL_LOCATION,
  LAST_VISITED_PINCODE,
  LAST_VISITED_PLACE_TYPES,
  SELECTED_SAVED_ADDRESS,
  SELECTED_SAVED_ADDRESS_USERNAME,
} from "../../../../constants/storage";
import { usePathname } from "next/navigation";
import { requestToFetchProductListingAction } from "../../../../store/slices/plpSlice";
import { plpPageConstants } from "../../../../constants/pageConstants/plpConstants";
import { COUNTRIES } from "../../../../constants/common/common";
import { removeEmptyNullAndUndefinedFromObject } from "../../../../utils/common";
import {
  calDiscountPercentage,
  getProductDeliveryType,
  pageName,
  removeHashtagFromURL,
} from "../../../../utils/common";
import { AUTO_TRIGGER_LOCK, FROM_LOCATION_LOCK } from "../../../../constants/common/locationFieldConstants";
import { unlockBody } from "../../../../utils/document";
import useAnalytics from "../../../../analytics/useAnalytics";
import { cleverTapCommonEvents } from "../../../../analytics/clevertapConstants";
import { CAKE_TYPE } from "../../../../constants/pageConstants/pdpConstants";
import {
  getGateAutoTriggerFlag,
  capitalizeWords,
  getLocationSavedTrackingFields,
  setLastVisitedLatLong,
} from "../../../../utils/locationLock";
import { setPincodeErrorFromAddToCart } from "../../../../store/slices/pdpSlice";
import { getCleverTapPageType } from "../../../../utils/fnpPageType";
import { dispatchHomeFeedRefresh } from "../../../../utils/dynamicHomeUtils/refreshHomeFeedAfterLocationChange";
import { isHomePathname } from "../../../../utils/dynamicHomeUtils/storeUrl";

/**
 * Description placeholder
 * @date 09/04/2024 - 10:32:28
 *
 * @export
 * @returns {string}
 */
export default function SavedAddress({ savedAddress, postalAddress }) {
  const dispatch = useDispatch();
  const pathname = usePathname();
  const { trackEvent } = useAnalytics();
  const [selectedAddress, setSelectedAddress] = useState(null);
  const completePathName = typeof window !== "undefined" && window.location.href ? window.location.href : null;
  const {
    appliedFilters,
    appliedSort,
    isMobile,
    catalogId,
    selectedCountryFromList,
    configData,
    product,
    clientVariantType,
    activeStore,
  } = useSelector((state) => {
    const stores = state.dynamicHome?.pageStores ?? [];
    return {
      appliedFilters: state?.plp?.appliedFilters,
      appliedSort: state?.plp?.appliedSort,
      isMobile: state?.appConfig?.isMobile,
      catalogId: state.appConfig.catalogId,
      selectedCountryFromList: state?.locationLock?.selectedCountryFromList,
      configData: state?.locationLock?.configData,
      product: state?.pdp?.clientCurrentClickedItem,
      clientVariantType: state?.pdp?.clientVariantType,
      activeStore: stores.find((s) => s.isSelected) ?? stores[0] ?? null,
    };
  }, shallowEqual);

  const {
    isAutoGateEnabledOnDesktopHome,
    isAutoGateMandateOnDesktopHome,
    isAutoGateMandateOnDesktopPLP,
    isAutoGateEnabledOnDesktopPLP,
    isAutoGateEnabledOnMobileHome,
    isAutoGateEnabledOnMobilePLP,
    isAutoGateMandateOnMobileHome,
    isAutoGateMandateOnMobilePLP,
    autoTriggerLock: autoTriggerLockTime,
    mandateGatePLPList,
  } = configData;
  const urlIdentifier = product?.urlIdentifier;
  const isHome = isHomePathname(pathname);
  const isPLP = pathname?.includes("-lp");
  const isFromCheckout = pathname?.includes("checkout/delivery-address");
  const isAutoTriggerLock = getGateAutoTriggerFlag(mandateGatePLPList);
  const isInternational = catalogId !== COUNTRIES.INDIA;
  const isMandateForDesktop =
    !isAutoTriggerLock &&
    catalogId === COUNTRIES.INDIA &&
    selectedCountryFromList === COUNTRIES.INDIA &&
    ((isAutoGateMandateOnDesktopHome && isHome) || (isAutoGateMandateOnDesktopPLP && isPLP));
  const isMandateForMobile =
    !isAutoTriggerLock &&
    catalogId === COUNTRIES.INDIA &&
    selectedCountryFromList === COUNTRIES.INDIA &&
    ((isAutoGateMandateOnMobileHome && isHome) || (isAutoGateMandateOnMobilePLP && isPLP));
  const isAutoForDesktop =
    !isAutoTriggerLock && ((isAutoGateEnabledOnDesktopHome && isHome) || (isAutoGateEnabledOnDesktopPLP && isPLP));
  const isAutoForMobile =
    !isAutoTriggerLock && ((isAutoGateEnabledOnMobileHome && isHome) || (isAutoGateEnabledOnMobilePLP && isPLP));
  const commonPageData = {
    page_name: pageName(isHome, isPLP, pathname, urlIdentifier),
    pageType: (typeof window !== "undefined" && window.fnpPageType) || "",
    mandatory: isAutoTriggerLock ? false : isMobile ? isMandateForMobile : isMandateForDesktop,
    auto: isAutoTriggerLock ? false : isMobile ? isAutoForMobile : isAutoForDesktop,
    user_initiated: isAutoTriggerLock
      ? true
      : isMobile
        ? !(isAutoForMobile || isMandateForMobile)
        : !(isAutoForDesktop || isMandateForDesktop),
    clean_url: pathname,
    complete_url: completePathName,
  };
  const pageLocationSavedData = {
    ...commonPageData,
    area: null,
    pincode: null,
    system_pincode: null,
    country: null,
    city: null,
  };
  const discountPercentage = calDiscountPercentage(product?.productPrice?.listPrice, product?.productPrice?.price);
  const productDeliveryType = getProductDeliveryType(product?.primaryCategoryId);
  const lastVisitedAddress = CookieUtil.getCookie("lastVisitedAddress");
  const lastVisitedPincode = CookieUtil.getCookie("lastVisitedPincode");
  const lastVisitedIntLocation = CookieUtil.getCookie(LAST_VISITED_INTERNATIONAL_LOCATION);
  const cleverTapPDPCommonData = {
    page_name: pageName(isHome, isPLP, pathname, urlIdentifier),
    pageType: (typeof window !== "undefined" && window.fnpPageType) || "",
    product_name: isFromCheckout ? "" : product?.productName,
    pid: isFromCheckout ? "" : product?.productId,
    product_category: isFromCheckout ? "" : product?.primaryCategoryId,
    product_price: isFromCheckout ? "" : product?.productPrice?.price,
    is_variant_available: isFromCheckout ? "" : product?.isVariant === "Y",
    is_egg: isFromCheckout ? "" : (clientVariantType?.toUpperCase() === CAKE_TYPE.WITH_EGG ?? false),
    delivery_type: isFromCheckout ? "" : productDeliveryType,
    is_international: isInternational,
    city: isInternational ? lastVisitedIntLocation : "",
    area: isInternational ? "" : lastVisitedAddress,
    pincode: isInternational ? "" : lastVisitedPincode,
    system_pincode: isInternational ? "" : lastVisitedPincode,
    country: catalogId,
    strike_price: isFromCheckout ? "" : product?.productPrice?.listPrice,
    discount_percentage: isFromCheckout ? "" : discountPercentage,
    product_rating: isFromCheckout ? "" : product?.productReviewsAndRatings?.ratingValue,
    product_review_count: isFromCheckout ? "" : product?.productReviewsAndRatings?.totalReview,
    is_product_available: isFromCheckout ? "" : product?.stockAvailable,
    sku_id: isFromCheckout ? "" : product?.skuCode,
    clean_url: pathname,
    complete_url: completePathName,
  };
  const ishomePlpPdpLocationData = isHome || isPLP ? pageLocationSavedData : cleverTapPDPCommonData;

  useEffect(() => {
    const userSelectedAddress = savedAddress.find((item) => item?.postalAddress === postalAddress);
    setSelectedAddress(userSelectedAddress);
  }, [postalAddress, savedAddress]);

  /** */
  const onHandleAddressSelection = (item) => {
    const formattedAddress = [item?.address, item?.address2, item?.city, item?.postalcode]
      .filter(Boolean)
      .map(capitalizeWords)
      .join(", ");

    setSelectedAddress(item);

    const options = {
      maxAge: 60 * 24 * 60 * 30,
      path: "/",
      encode: decodeURIComponent,
    };

    CookieUtil.setCookie(LAST_VISITED_PINCODE, item?.postalcode, options);
    CookieUtil.setCookie(LAST_VISITED_ADDRESS, formattedAddress, options);
    // Saved address, not a Google place: the location field shows its pincode again.
    CookieUtil.deleteCookie(LAST_VISITED_PLACE_TYPES, { path: "/" });
    setLastVisitedLatLong(item?.latitude, item?.longitude, item?.accuracyType);
    CookieUtil.setCookie(SELECTED_SAVED_ADDRESS, item?.postalAddress, options);
    CookieUtil.setCookie(SELECTED_SAVED_ADDRESS_USERNAME, item?.toName, options);
    dispatch(
      setSelectedDomesticLocation({
        pincode: item?.postalcode,
        address: formattedAddress,
      }),
    );
    dispatch(setIsShowDesktopLocationLockAction(false));
    dispatch(setIsShowMobileLocationLockAction(false));
    dispatch(setPincodeErrorFromAddToCart(false));
    removeHashtagFromURL(FROM_LOCATION_LOCK);
    dispatch(setIsSavedAddressUserName(item?.toName));
    if ((isHome || isPLP) && autoTriggerLockTime) {
      CookieUtil.setCookie(AUTO_TRIGGER_LOCK, autoTriggerLockTime.toString(), {
        maxAge: autoTriggerLockTime,
        path: "/",
        encode: decodeURIComponent,
        secure: true,
      });
    }
    if (pathname?.includes("-lp")) {
      window.scrollTo({ top: 0 });
      // Get email from faul cookie
      const email = CookieUtil.getCookie("faul") || "";
      dispatch(
        requestToFetchProductListingAction(
          removeEmptyNullAndUndefinedFromObject({
            page: 0, //details?.currentPage,
            size: isMobile ? plpPageConstants.MOBILE_PRODUCT_PER_PAGE : plpPageConstants.DESKTOP_PRODUCT_PER_PAGE,
            geoId: COUNTRIES?.INDIA,
            categoryUrl: `fnp.com${pathname}`,
            pincode: item?.postalcode,
            filters: appliedFilters,
            sortBy: appliedSort?.sortBy,
            sortOrder: appliedSort?.sortOrder,
            email,
          }),
        ),
      );
    }

    if (isHome) {
      dispatchHomeFeedRefresh(dispatch, {
        isMobile,
        pincode: item?.postalcode,
        storeId: activeStore?.storeId,
        storeName: activeStore?.storeName || activeStore?.name,
      });
    }

    unlockBody();
    const { pageType, ...restLocationData } = ishomePlpPdpLocationData || {};
    trackEvent(
      cleverTapCommonEvents.locationSaved,
      {
        ...restLocationData,
        page_type: getCleverTapPageType(pageType || restLocationData?.page_type),
        area: item?.city,
        pincode: item?.postalcode,
        system_pincode: item?.postalcode,
        flow: "new",
        ...getLocationSavedTrackingFields("saved_address", item?.accuracyType),
      },
      ["clevertap"],
    );
  };
  return (
    <div
      className={`mb-16 flex max-h-[37vh] transform flex-col overflow-auto rounded-[16px] border border-thinLightGrey bg-white-900 pb-16 scrollbar-thin scrollbar-webkit md:max-h-[36vh]  md:p-16`}
    >
      <div
        className={`flex w-full ${isMobile ? "border-b-1 border-lightGrey" : ""}  items-center p-16 md:mb-16 md:p-0`}
      >
        <div className="mr-8">
          <CustomImage
            src="/icons/user-square-desktop.svg"
            alt=""
            sizes="10vw"
            width={24}
            height={24}
            isPriority={false}
          />
        </div>
        <Typography variant="p" Tag="p" classes="font-600 text-16 text-fnp-500">
          Saved Addresses
        </Typography>
      </div>
      <div className="w-full">
        {savedAddress
          ?.filter((item) => item?.countryGeoId === "IND")
          .map((item, index) => {
            return (
              <RadioItemList
                id={`${item.toName}_${index}`}
                name={`${item.toName}_${index}`}
                radioListType="savedAddress"
                listContent={item}
                key={`${item.toName}_${index}`}
                isChecked={selectedAddress === item}
                // eslint-disable-next-line react/jsx-handler-names
                onChange={() => {
                  onHandleAddressSelection(item);
                }}
                parentClass={`${isMobile ? "p-16" : "px-12 py-16"} ${index === savedAddress.length - 1 ? "pb-0" : "border-b-1"}`}
              />
            );
          })}
      </div>
    </div>
  );
}
SavedAddress.propTypes = {
  savedAddress: PropTypes.array.isRequired,
  postalAddress: PropTypes.string,
};
